/**
 * Sitenin bağlantı haritası — TEK kaynak.
 *
 * ---------------------------------------------------------------------------
 * Neden böyle bir dosya var
 * ---------------------------------------------------------------------------
 * Şu an anasayfa, /login, /register ve /forgot-password yayında. Footer'ın
 * gösterdiği on beş
 * sayfanın hiçbiri henüz açılmadı. Bu sayfalara `#` ya da gerçek görünen bir
 * adres vermek, kullanıcının tıklayıp hiçbir şey olmadığını (ya da 404
 * gördüğünü) yaşaması demek — site bozuk sanılır. Arama motoru için de
 * zararlı: var olmayan adrese giden bağlantı taranıp ölü bulunuyor.
 *
 * Bu yüzden adres ile "sayfa gerçekten var mı" bilgisi yan yana duruyor.
 * `live: false` olan kayıt bağlantı olarak BASILMAZ; footer onu tıklanamayan
 * düz metin + "yakında" etiketiyle gösterir. Sayfa yayına girdiğinde tek
 * yapılacak iş buradaki bayrağı `true` çevirmek — bileşene dokunulmaz.
 *
 * ---------------------------------------------------------------------------
 * Kullanım kuralları
 * ---------------------------------------------------------------------------
 * • Adresler dil öneksiz yazılır. Bağlantıyı `@/i18n/navigation`'daki `Link`
 *   basıyor, /tr önekini o ekliyor.
 * • Yeni bir sayfa eklerken önce buraya kayıt açılır, sonra bileşende
 *   anahtarıyla çağrılır. Bileşene ham adres yazılmaz.
 *
 * NOT (9 Ağu 2026): `site-header.tsx`, `cta.tsx` ve `hero.tsx` bu haritayı
 * henüz kullanmıyor; adreslerini gömüyorlar ve açılmamış sayfalara (/pricing,
 * /features/*) gerçek bağlantı veriyorlar. Onların da buraya bağlanması ayrı
 * bir iş olarak duruyor — bu dosya o iş yapılırken genişletilecek yer.
 *
 * Auth ekranları (giriş / kayıt / şifre sıfırlama) bağlandı. Tek istisna
 * yasal bağlantılar (/terms, /privacy): sayfalar `live: false` ama yasal metin
 * bağlantı OLMAK zorunda, "yakında" düz metnine dönüşemez.
 */

export type SiteLink = {
  /** Dil öneksiz adres. Önek `Link` tarafından ekleniyor. */
  href: string;
  /** Sayfa gerçekten var mı? `false` → bağlantı basılmaz, düz metin olur. */
  live: boolean;
};

export const siteLinks = {
  /* --- Yayında --- */
  home: { href: '/', live: true },
  login: { href: '/login', live: true },
  register: { href: '/register', live: true },
  forgotPassword: { href: '/forgot-password', live: true },
  /* Tek kullanımlık bağlantının vardığı ekran. Haritada duruyor çünkü sayfa
     gerçekten var; ama kimse buraya BAĞLANMIYOR — adresi e-posta taşıyor ve
     token'sız açıldığında ekran "bağlantı geçersiz" diyor. */
  resetPassword: { href: '/reset-password', live: true },

  /* --- Ürün: dört modül. Adresler header'ın mega menüsüyle aynı. --- */
  featureAnalyze: { href: '/features/analyze', live: false },
  featureWorkflow: { href: '/features/workflow', live: false },
  featureAgent: { href: '/features/agent', live: false },
  featureCreative: { href: '/features/creative', live: false },

  /* --- Kaynaklar ---
     `blog` 12 Ağu 2026'da dizin olarak yayına girdi; yazı sayfaları
     (`/blog/<slug>`) 13 Ağu 2026'da açıldı ve kartlar artık bağlantı.
     Yazının kendisi bu haritada YOK ve olmayacak: adresler veritabanındaki
     slug'lardan doğuyor, burada listelenseydi her yeni yazı bir kod
     değişikliği isterdi. Bu harita sayfaları anlatıyor, içeriği değil. */
  blog: { href: '/blog', live: true },
  docs: { href: '/docs', live: false },
  changelog: { href: '/changelog', live: false },
  help: { href: '/help', live: false },

  /* --- Şirket ---
     `about` 10 Ağu 2026'da yayına girdi (app/[locale]/about/page.tsx); footer
     artık onu "yakında" düz metni yerine gerçek bağlantı olarak basıyor. */
  about: { href: '/about', live: true },
  contact: { href: '/contact', live: false },
  pricing: { href: '/pricing', live: false },

  /* --- Yasal ---
     Adresler düz (/privacy, /terms) — /legal/* değil. Sebep: login formu
     zaten bu iki adrese bağlanıyor (components/login-form.tsx), iki ayrı
     yol düzeni tutmanın anlamı yok. `dataProtection` KVKK ve GDPR'ı tek
     sayfada topluyor; slug dil bağımsız olsun diye "data-protection". */
  privacy: { href: '/privacy', live: false },
  terms: { href: '/terms', live: false },
  dataProtection: { href: '/data-protection', live: false },
  cookies: { href: '/cookies', live: false },
} satisfies Record<string, SiteLink>;

export type SiteLinkKey = keyof typeof siteLinks;
